import type {
  ActivationOrderAdminView,
  ActivationOrderView,
  ActivationPaymentEventView,
  FreeActivationClaimView,
  FreeActivationStatusView,
  AdminState,
  ApiResult,
  DisplaySettingsView,
  InviteSummary,
  ListenerGrant,
  ListenerState,
  OwnerBootstrapResult,
  OwnerSession,
  ScheduleMatch,
  ScheduleMatchInput,
  ScheduleSuggestions,
  StreamView
} from '@ai-voice/contracts';

type AuditRecordView = {
  id: string;
  at: string;
  action: string;
  actor: string;
  target?: string;
  detail: Record<string, unknown>;
};

type BanView = {
  deviceId: string;
  userId: string;
  reason: string;
  subjectType?: 'device' | 'ip';
  banKind?: 'access' | 'payment';
  displayValue?: string;
  createdAt: string;
  active: boolean;
  liftedAt?: string;
};

export class ApiError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly retryAfterSeconds?: number
  ) {
    super(message);
  }
}

export function normalizeLatencyMs(value: number | null | undefined): number | undefined {
  if (typeof value !== 'number' || !Number.isFinite(value)) return undefined;
  return Math.min(60_000, Math.max(0, Math.round(value)));
}

const API_BASE = (import.meta.env.VITE_API_BASE_URL ?? '').replace(/\/$/, '');

async function request<T>(path: string, init: RequestInit = {}, token?: string): Promise<T> {
  const headers = new Headers(init.headers);
  headers.set('content-type', 'application/json');
  if (token) headers.set('authorization', `Bearer ${token}`);

  const response = await fetch(`${API_BASE}${path}`, {
    ...init,
    headers,
    signal: init.signal ?? AbortSignal.timeout(12_000)
  });
  const result = (await response.json()) as ApiResult<T>;
  if (!result.ok) throw new ApiError(result.error.code, result.error.message, result.error.retryAfterSeconds);
  return result.data;
}

export function getStoredOwnerSession(): OwnerSession | null {
  const raw = localStorage.getItem('ai-voice-owner-session');
  if (!raw) return null;
  try {
    return JSON.parse(raw) as OwnerSession;
  } catch {
    localStorage.removeItem('ai-voice-owner-session');
    return null;
  }
}

export function saveOwnerSession(session: OwnerSession): void {
  localStorage.setItem('ai-voice-owner-session', JSON.stringify(session));
}

export function clearOwnerSession(): void {
  localStorage.removeItem('ai-voice-owner-session');
}

export const api = {
  ownerStatus: () => request<{ initialized: boolean }>('/v1/owner/status'),
  bootstrap: (input: { bootstrapToken: string; username: string; password: string }) =>
    request<OwnerBootstrapResult>('/v1/owner/bootstrap', { method: 'POST', body: JSON.stringify(input) }),
  login: (input: { username: string; password: string; totp: string }) =>
    request<OwnerSession>('/v1/owner/login', { method: 'POST', body: JSON.stringify(input) }),
  refresh: (refreshToken: string) =>
    request<OwnerSession>('/v1/owner/refresh', { method: 'POST', body: JSON.stringify({ refreshToken }) }),

  startStream: (token: string) =>
    request<{ roomId: string; sessionId: string; sdkAppId: number; userId: string; userSig: string; privateMapKey: string; startedAt: string }>(
      '/v1/stream/start', { method: 'POST' }, token
    ),
  streams: () => request<StreamView[]>('/v1/streams'),
  startMatchStream: (token: string, matchId: string, captureMode: 'system' | 'tab') =>
    request<{ roomId: string; sessionId: string; sdkAppId: number; userId: string; userSig: string; privateMapKey: string; startedAt: string }>(
      '/v1/streams/start',
      { method: 'POST', body: JSON.stringify({ matchId, captureMode }) },
      token
    ),
  streamHeartbeat: (token: string, sessionId?: string) =>
    sessionId
      ? request<{ active: boolean }>(`/v1/streams/${encodeURIComponent(sessionId)}/heartbeat`, { method: 'POST' }, token)
      : request<{ active: boolean }>('/v1/stream/heartbeat', { method: 'POST' }, token),
  rejoinMatchStream: (token: string, sessionId: string) =>
    request<{ roomId: string; sessionId: string; sdkAppId: number; userId: string; userSig: string; privateMapKey: string; startedAt: string }>(
      `/v1/streams/${encodeURIComponent(sessionId)}/rejoin`,
      { method: 'POST' },
      token
    ),
  stopMatchStream: (token: string, sessionId: string) =>
    request<{ stopped: boolean }>(`/v1/streams/${encodeURIComponent(sessionId)}/stop`, { method: 'POST' }, token),
  stopAllStreams: (token: string) => request<{ stopped: number }>('/v1/streams/stop-all', { method: 'POST' }, token),

  stopStream: (token: string) => request<{ stopped: boolean }>('/v1/stream/stop', { method: 'POST' }, token),
  createInvite: (token: string, ttlSeconds: number) =>
    request<{ inviteId: string; code: string; expiresAt: string; ttlSeconds: number }>(
      '/v1/invites',
      { method: 'POST', body: JSON.stringify({ ttlSeconds }) },
      token
    ),
  activationCodes: (token: string, source?: 'owner' | 'purchase' | 'free', query?: string) => {
    const params = new URLSearchParams();
    if (source) params.set('source', source);
    if (query?.trim()) params.set('query', query.trim());
    const suffix = params.size ? `?${params.toString()}` : '';
    return request<InviteSummary[]>(`/v1/admin/activation-codes${suffix}`, {}, token);
  },
  listInvites: (token: string) => request<InviteSummary[]>('/v1/invites', {}, token),
  revokeInvite: (token: string, inviteId: string) =>
    request<{ revoked: boolean }>(`/v1/invites/${encodeURIComponent(inviteId)}`, { method: 'DELETE' }, token),
  displaySettings: () => request<DisplaySettingsView>('/v1/public/display-settings'),
  schedule: () => request<ScheduleMatch[]>('/v1/schedule'),
  adminSchedule: (token: string) => request<ScheduleMatch[]>('/v1/admin/schedule', {}, token),
  scheduleSuggestions: (token: string) => request<ScheduleSuggestions>('/v1/admin/schedule/suggestions', {}, token),
  createScheduleMatch: (token: string, input: ScheduleMatchInput) =>
    request<ScheduleMatch>('/v1/admin/schedule', { method: 'POST', body: JSON.stringify(input) }, token),
  updateScheduleMatch: (token: string, id: string, input: ScheduleMatchInput) =>
    request<ScheduleMatch>(`/v1/admin/schedule/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(input) }, token),
  deleteScheduleMatch: (token: string, id: string) =>
    request<{ deleted: boolean }>(`/v1/admin/schedule/${encodeURIComponent(id)}`, { method: 'DELETE' }, token),
  adminState: (token: string) => request<AdminState>('/v1/admin/state', {}, token),
  participantAction: (token: string, sessionId: string, action: 'pause' | 'resume' | 'kick' | 'ban', reason?: string) =>
    request<{ action: string; sessionId: string }>(
      `/v1/admin/participants/${encodeURIComponent(sessionId)}/${action}`,
      { method: 'POST', body: JSON.stringify(reason ? { reason } : {}) },
      token
    ),
  listBans: (token: string) => request<BanView[]>('/v1/admin/bans', {}, token),
  unban: (token: string, deviceId: string) =>
    request<{ unbanned: boolean }>(`/v1/admin/devices/${encodeURIComponent(deviceId)}/unban`, { method: 'POST' }, token),
  updateDisplaySettings: (token: string, input: {
    virtualOnlineBase?: number;
    scrollingMessage?: string;
    paidActivationEnabled?: boolean;
    activationPriceDay?: number;
    activationPriceWeek?: number;
    activationPriceMonth?: number;
    freeActivationEnabled?: boolean;
    freeActivationStartDate?: string;
    freeActivationEndDate?: string;
  }) =>
    request<DisplaySettingsView>(
      '/v1/admin/display-settings',
      { method: 'PATCH', body: JSON.stringify(input) },
      token
    ),
  updateUsageLimit: (token: string, monthlyLimitMinutes: number) =>
    request<{ updated: boolean }>(
      '/v1/admin/usage-limit',
      { method: 'PATCH', body: JSON.stringify({ monthlyLimitMinutes }) },
      token
    ),
  auditEvents: (token: string) => request<AuditRecordView[]>('/v1/admin/audit', {}, token),

  redeem: (input: { code: string; deviceId: string; nickname: string; streamId?: string }) =>
    request<ListenerGrant>('/v1/listener/redeem', { method: 'POST', body: JSON.stringify(input) }),
  heartbeat: (input: { sessionId: string; sessionSecret: string; latencyMs?: number; playing: boolean }) =>
    request<ListenerState>('/v1/listener/heartbeat', { method: 'POST', body: JSON.stringify(input) }),
  leave: (sessionId: string, sessionSecret: string) =>
    request<{ left: boolean }>('/v1/listener/leave', { method: 'POST', body: JSON.stringify({ sessionId, sessionSecret }) }),
  freeActivationStatus: (deviceId: string) =>
    request<FreeActivationStatusView>(`/v1/activation/free-claim?deviceId=${encodeURIComponent(deviceId)}`),
  claimFreeActivation: (deviceId: string) =>
    request<FreeActivationClaimView>('/v1/activation/free-claim', {
      method: 'POST',
      body: JSON.stringify({ deviceId })
    }),
  createActivationOrder: (ttlSeconds: number, deviceId: string) =>
    request<ActivationOrderView>('/v1/activation/orders', {
      method: 'POST',
      body: JSON.stringify({ ttlSeconds, deviceId })
    }),
  activationOrder: (orderId: string, deviceId: string) =>
    request<ActivationOrderView>(`/v1/activation/orders/${encodeURIComponent(orderId)}?deviceId=${encodeURIComponent(deviceId)}`),
  adminActivationOrders: (token: string) => request<ActivationOrderAdminView[]>('/v1/admin/activation-orders', {}, token),
  adminActivationPaymentEvents: (token: string) =>
    request<ActivationPaymentEventView[]>('/v1/admin/activation-payment-events', {}, token),

  listenerState: (sessionId: string, sessionSecret: string) =>
    request<ListenerState>('/v1/listener/state', {
      headers: {
        'x-session-id': sessionId,
        'x-session-secret': sessionSecret
      }
    })
};




